import { ChangeEvent, Fragment, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactCropperImage from 'react-easy-crop';
import { ButtonPrimary } from '@components/button/button-primary';
import { ButtonSecondary } from '@components/button/button-secondary';
import { SwitchToggle } from '@components/switch-toggle';
import { DRAWER_OVERLAY_Z_INDEX } from '@constants/z-index';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';

import { getCroppedImg } from './utils/get-cropped-img';
import type { Area, SaveState } from './cropper.type';
import { CropperAspectOption, CropperProps } from './cropper.type';

const getInitialAspectRatio = (
  fixed: number | undefined,
  options: readonly CropperAspectOption[] | undefined
): number => {
  if (fixed !== undefined) return fixed;
  if (options && options.length > 0) {
    const defaultOption = options.find((opt) => opt.isDefault) ?? options[0];
    return defaultOption.value;
  }
  return 1;
};

const getNextAspectRatio = (current: number, options: readonly CropperAspectOption[]): number => {
  const currentIndex = options.findIndex((opt) => opt.value === current);
  const nextIndex = (currentIndex + 1) % options.length;
  return options[nextIndex].value;
};

export const Cropper = (props: CropperProps) => {
  const { open, image, onSave, onCancel, onError, translations, imageFormat } = props;
  const aspectRatios = 'aspectRatios' in props ? props.aspectRatios : undefined;
  const fixedAspectRatio = 'aspectRatio' in props ? props.aspectRatio : undefined;
  const isToggleMode = !!aspectRatios;

  const initialRatio = useMemo(
    () => getInitialAspectRatio(fixedAspectRatio, aspectRatios),
    [fixedAspectRatio, aspectRatios]
  );

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [activeAspectRatio, setActiveAspectRatio] = useState(initialRatio);
  // Four states rather than a flag per question: `saved` and `consumerFailed` both mean "the
  // image is out, Save must not run again", but only the second is an exit the user needs.
  const [saveState, setSaveState] = useState<SaveState>('idle');
  // `onSave` appends an image per call, so it must run at most once per open. State cannot
  // enforce that: two handlers running in the same tick both read the pre-update value. A ref is
  // written synchronously, which is what "at most once" needs.
  const saveStartedRef = useRef(false);

  // Layout, not passive: `drag-and-drop` keeps the cropper mounted between opens, so a passive
  // reset paints one frame still carrying the previous image's crop area — with Save enabled.
  useLayoutEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setActiveAspectRatio(initialRatio);
      setSaveState('idle');
      saveStartedRef.current = false;
    }
  }, [open, initialRatio]);

  const onCropComplete = useCallback((_: unknown, area: Area) => {
    setCroppedAreaPixels(area);
  }, []);

  const reportError = (error: unknown, fallbackMessage: string) => {
    const err = error instanceof Error ? error : new Error(String(error));
    if (onError) onError(err);
    else console.error(fallbackMessage, err);
  };

  const handleSave = async () => {
    if (saveStartedRef.current || !croppedAreaPixels) return;
    saveStartedRef.current = true;
    setSaveState('saving');

    let cropped: File | null = null;
    try {
      const outputType = imageFormat ?? image.file.type;
      const result = await getCroppedImg(image.src, outputType, croppedAreaPixels, rotation);
      cropped = result?.file ?? null;
    } catch (error: unknown) {
      reportError(error, 'Failed to crop image');
    }

    if (!cropped) {
      // Nothing was handed over, so let the user retry.
      saveStartedRef.current = false;
      setSaveState('idle');
      return;
    }

    setSaveState('saved');
    // Called outside the crop `try` on purpose: once the image has been handed over, a throw
    // from the consumer must not re-arm the guard — a retry would append a second image — and
    // must not be reported as a cropping failure.
    try {
      onSave(cropped, { aspectRatio: activeAspectRatio });
    } catch (error: unknown) {
      reportError(error, 'The cropper consumer failed to handle the cropped image');
      // Save stays locked, but the user has to be able to leave: `drag-and-drop` calls `onChange`
      // BEFORE closing the dialog, so a throw there means nothing closed it. Its `onCancel`
      // reverting the file is the right recovery in this branch — the save failed, so there is
      // nothing to undo.
      setSaveState('consumerFailed');
    }
  };

  const handleZoom = (e: ChangeEvent<HTMLInputElement>) => setZoom(Number.parseFloat(e.target.value));
  const handleRotate = (e: ChangeEvent<HTMLInputElement>) => setRotation(Number.parseFloat(e.target.value));

  const handleAspectToggle = () => {
    if (!aspectRatios) return;
    const nextRatio = getNextAspectRatio(activeAspectRatio, aspectRatios);
    setActiveAspectRatio(nextRatio);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
  };

  // Anything that could produce a second `onSave` is closed from the moment the first one starts.
  const isSaveLocked = saveState !== 'idle';
  // Cancel and the dialog's own close are locked too, but for a different reason and not in every
  // state: `drag-and-drop`'s `onCancel` reverts the file its `onSave` just set, so a press during
  // the leave transition would undo a save that succeeded. After a consumer failure they unlock —
  // there the revert is the recovery, and it is the only way out of a dialog nothing closed.
  const isExitLocked = saveState === 'saving' || saveState === 'saved';

  const isDefaultRatio =
    isToggleMode && aspectRatios
      ? activeAspectRatio === (aspectRatios.find((opt) => opt.isDefault) ?? aspectRatios[0]).value
      : false;

  // Enter is a keyframe: Transition's enter classes land a frame after mount, so the dialog flashed opaque.
  return (
    <Transition show={open} as={Fragment} appear={true}>
      <Dialog
        className='relative'
        style={{ zIndex: DRAWER_OVERLAY_Z_INDEX }}
        open={open}
        onClose={isExitLocked ? () => {} : onCancel}>
        <TransitionChild as={Fragment} leave='ease-in duration-200' leaveFrom='opacity-100' leaveTo='opacity-0'>
          <div className='fixed inset-0 backdrop-overlay transition-opacity animate-fade-in' aria-hidden='true' />
        </TransitionChild>

        <div className='fixed inset-0 flex items-center justify-center p-4'>
          <TransitionChild
            as={Fragment}
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'>
            <DialogPanel className='bg-surface-primary rounded-lg w-full max-w-2xl shadow-xl transform transition-all animate-dialog-in'>
              <div className='px-4 pt-5 pb-4 sm:p-6'>
                <div className='relative w-full h-96'>
                  <ReactCropperImage
                    image={image.src}
                    crop={crop}
                    rotation={rotation}
                    zoom={zoom}
                    aspect={activeAspectRatio}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>

                {isToggleMode && translations.aspectRatioToggleLabel && (
                  <div className='mt-4 sm:mt-6 flex items-center gap-3'>
                    <SwitchToggle
                      active={isDefaultRatio}
                      loading={false}
                      onToggleSwitch={handleAspectToggle}
                      disabled={isSaveLocked}
                    />
                    <div className='flex flex-col'>
                      <span className='text-secondary text-subtitle-s'>{translations.aspectRatioToggleLabel}</span>
                      {translations.aspectRatioToggleSublabel && (
                        <span className='text-tertiary text-body-xs'>{translations.aspectRatioToggleSublabel}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className='mt-4 sm:mt-6 grid grid-rows-2 md:grid-rows-1 md:grid-cols-2 gap-y-4 gap-x-8'>
                  <div className='flex flex-row space-x-4'>
                    <label className='text-primary' htmlFor='cropper-zoom'>
                      {translations.zoom}
                    </label>
                    <input
                      id='cropper-zoom'
                      name='zoom'
                      className='text-primary'
                      value={zoom}
                      type='range'
                      onChange={handleZoom}
                      min='1'
                      max='3'
                      step='0.1'
                    />
                  </div>

                  <div className='flex flex-row space-x-4'>
                    <label className='text-primary' htmlFor='cropper-rotate'>
                      {translations.rotate}
                    </label>
                    <input
                      id='cropper-rotate'
                      name='rotate'
                      value={rotation}
                      type='range'
                      onChange={handleRotate}
                      min={0}
                      max={360}
                      step={1}
                    />
                  </div>
                </div>

                <div className='flex justify-end mt-4 sm:mt-6 gap-4'>
                  <ButtonSecondary
                    type='button'
                    data-testid='cropper__cancel'
                    onClick={onCancel}
                    disabled={isExitLocked}>
                    {translations.cancel}
                  </ButtonSecondary>

                  {/* Mirrors handleSave's own `!croppedAreaPixels` guard, which was silently
                      inert before the crop settled (B2B-2155). */}
                  <ButtonPrimary
                    type='button'
                    data-testid='cropper__save'
                    onClick={handleSave}
                    disabled={isSaveLocked || !croppedAreaPixels}>
                    {translations.save}
                  </ButtonPrimary>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
